/**
 * `POST /v1/checkout/quote` e `GET /v1/delivery/zones` — a cotacao e a tabela de frete.
 *
 * Fontes: `CONTRACTS §3.1` (as 11 etapas), `§3.2` (arredondamento e rateio), `§3.4` (os nomes
 * canonicos de saida), `§6` (TTL de 10 min, `QUOTE_EXPIRED`/`PRICE_CHANGED`), `§8.2` (as duas
 * linhas de rota), `API §6.10` (zonas), `§6.12` (checkout) e `§6.20.6` (o desenho da resposta).
 *
 * ## A decisao que governa a REQUISICAO: ela carrega **referencia e intencao**, nunca valor
 *
 * `QuoteRequestSchema` nao tem — e nao pode ganhar — `unitPriceCents`, `discountCents`,
 * `deliveryFeeCents`, `totalCents` nem `Money`. Ele tem o `cartId`, a modalidade, o endereco
 * (por id), ate um cupom (por codigo) e uma **dica** de meio de pagamento. Nada mais.
 *
 * > `SECURITY §8.1`, regra 1: *"Campo de preco/desconto/frete vindo do cliente (ex.:
 * > `unitPrice`, `discountCents`) e rejeitado com 400 pelo `.strict()`"*.
 *
 * O `.strict()` aqui nao e higiene: e o controle SEC-06/SEC-10 na borda. Um corpo com campo
 * monetario **e recusado**, e nao silenciosamente descartado — porque "descartado em silencio"
 * e indistinguivel de "aceito e perdido", e so a recusa deixa rastro no teste.
 *
 * ## A decisao que governa a RESPOSTA: todo numero nasce no servidor
 *
 * Cada centavo de `QuoteResponse` sai de `quotePrice()` (`@farmacia/domain/pricing`), que roda
 * as 11 etapas de `CONTRACTS §3.1` sobre dados do banco. Os nomes dos campos de total sao os
 * de `CONTRACTS §3.4` — `subtotalCents`, `itemDiscountsCents`, `couponDiscountCents`,
 * `shippingCents`, `shippingDiscountCents`, `totalCents`, `savingsCents` — embrulhados em
 * `Money` porque `API §1.8` manda dinheiro no wire ir com moeda junto.
 *
 * ## DUVIDAS declaradas (divergencias reportadas)
 *
 * 1. **`couponCodes: string[]` (plural) do `API §6.20.6` × "1 cupom por pedido".**
 *    `CONTRACTS §3.3` e a tabela unica de cumulatividade e diz, na linha "Cupom × cupom":
 *    **"Nunca (1 cupom por pedido)"**; `DOMAIN §4.1` INV-C8 repete no agregado. A precedencia
 *    (`CONTRACTS §1.1`) resolve a favor do singular. O campo mantem o **nome plural** do
 *    contrato de API (para o PWA nao ter de mudar a chave) com `.max(1)`: dois codigos sao
 *    `VALIDATION_ERROR`, nunca "aplica o melhor" — escolher por conta seria decidir desconto
 *    sem regra escrita.
 *
 * 2. **`contractSummary` do `API §6.20.6` NAO esta aqui.** Ele exige `policyVersion` e
 *    `withdrawalRightText` (Decreto 7.962/2013 art. 2º, REG-063), e **nao ha fonte**: nao ha
 *    tabela de politica legal versionada em `CONTRACTS §8.1` (as 53) nem chave no
 *    `DeploymentProfile`; a rota `GET /v1/legal/policies/{slug}` existe sem modelo por tras.
 *    Inventar o texto aqui seria produzir declaracao juridica a partir de nada. Reportado: o
 *    bloco entra quando a fonte existir, e o gate e `POST /v1/orders`, que e a tela do botao
 *    de confirmar.
 *
 * 3. **`scheduledSlotId` / `scheduled`** ficam `null`: `GET /v1/delivery/slots` e `P`
 *    (`API §6.10`). O campo existe na resposta, sempre nulo, porque a UI ja o le.
 */
import { z } from 'zod';

import { AvailabilityBandSchema, PaymentMethodSchema } from '../enums';
import { InstantSchema, MoneySchema, NonNegativeMoneySchema, Uuidv7Schema } from '../common';

import { CartFulfillmentModeSchema, CouponCodeSchema } from './cart';

// ---------------------------------------------------------------------------
// Cache e TTL
// ---------------------------------------------------------------------------

/**
 * `API §6.20.6`, ultima linha do bloco: **`Cache: no-store`**.
 *
 * `private` acompanha pelo mesmo motivo do carrinho: a cotacao e de um cliente, de um
 * carrinho e de um instante. Uma borda que a guardasse por engano entregaria o total de um
 * visitante ao proximo.
 */
export const CHECKOUT_QUOTE_CACHE_CONTROL = 'private, no-store';

/** `API §6.10`: `GET /v1/delivery/zones` e `CDN, s-maxage=600`. Zona muda por implantacao. */
export const DELIVERY_ZONES_CACHE_CONTROL = 'public, s-maxage=600, stale-while-revalidate=86400';

/**
 * **10 minutos** — `CONTRACTS §6`, valor canonico, e ele **corrige** `DOMAIN RN-008` (15),
 * `SECURITY §8.1` (15) e `PWA §8.1` (~5). Nao e configuravel (`CONTRACTS §10.3`).
 *
 * O mesmo numero e o default de `DEFAULT_QUOTE_TTL_MINUTES` em `@farmacia/domain/pricing`.
 * Ele aparece duas vezes porque os dois pacotes sao publicaveis em separado; um teste de
 * conformidade compara os dois (`checkout.test.ts`), e e ele que impede a deriva.
 */
export const QUOTE_TTL_MINUTES = 10;

// ---------------------------------------------------------------------------
// Requisicao
// ---------------------------------------------------------------------------

/**
 * A modalidade pedida. Discriminada porque os dois ramos exigem coisas **diferentes**, e um
 * objeto com os dois campos opcionais permitiria pedir entrega sem endereco.
 *
 * `PICKUP` carrega `storeId` (`API §6.20.6`) e ele e conferido contra o carrinho: divergencia
 * e `CART_STORE_MISMATCH` (409), nao troca silenciosa de filial (INV-C1).
 */
export const QuoteFulfillmentRequestSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('DELIVERY'), addressId: Uuidv7Schema }).strict(),
  z.object({ mode: z.literal('PICKUP'), storeId: Uuidv7Schema }).strict(),
]);
export type QuoteFulfillmentRequest = z.infer<typeof QuoteFulfillmentRequestSchema>;

/**
 * O corpo de `POST /v1/checkout/quote`.
 *
 * **Leia a lista de campos como uma lista de ausencias.** Nao ha preco, desconto, frete nem
 * total; nao ha `items[]` com valor; nao ha `quantity` (a quantidade e a do carrinho, gravada
 * pelas rotas de `API §6.9`, e ja passou pelo teto sanitario de REG-013 la).
 *
 * `paymentMethodHint` e **dica**: `API §6.20.6` escreve "hint nao vincula". Ela existe porque
 * `CONTRACTS §3.1` etapa 0 poe o meio de pagamento pretendido no contexto; ela nao concede
 * desconto no MVP (nao ha mecanica de preco por meio de pagamento ligada) e nao reserva nada.
 */
export const QuoteRequestSchema = z
  .object({
    cartId: Uuidv7Schema,
    fulfillment: QuoteFulfillmentRequestSchema,
    /** Ver a DUVIDA 1 no cabecalho: nome plural do `API §6.20.6`, cardinalidade 1. */
    couponCodes: z.array(CouponCodeSchema).max(1).optional(),
    paymentMethodHint: PaymentMethodSchema.optional(),
  })
  .strict();
export type QuoteRequest = z.infer<typeof QuoteRequestSchema>;

/** `GET /v1/delivery/zones?storeId=...` — a lista e **por filial** (`API §6.10`). */
export const DeliveryZonesQuerySchema = z.object({ storeId: Uuidv7Schema }).strict();
export type DeliveryZonesQuery = z.infer<typeof DeliveryZonesQuerySchema>;

// ---------------------------------------------------------------------------
// Frete, prazo e zona
// ---------------------------------------------------------------------------

/**
 * As quatro naturezas de zona (`ZoneKind` do `DATA_MODEL`, semeado de
 * `stores[].zones[].kind`). Declarado aqui, e nao em `enums.ts`, porque nao ha contraparte em
 * `@farmacia/domain`: zona e **dado de implantacao**, nao regra sanitaria.
 */
export const DELIVERY_ZONE_KIND_VALUES = [
  'SEDE_CENTRO',
  'SEDE_BAIRRO',
  'DISTRITO_RURAL',
  'MUNICIPIO_VIZINHO',
] as const;
export const DeliveryZoneKindSchema = z.enum(DELIVERY_ZONE_KIND_VALUES);
export type DeliveryZoneKind = z.infer<typeof DeliveryZoneKindSchema>;

/**
 * O prazo, **sempre em faixa** (A-05, `API §6.20.6`: "SEMPRE faixa, nunca minuto exato").
 *
 * Prometer "chega em 37 minutos" e prometer o transito de Solonopole a um cliente que vai
 * conferir no relogio. A faixa e a promessa honesta, e o `label` e a forma de a UI nao ter de
 * montar a frase (e de o teste conseguir asserta-la).
 */
export const EtaBandSchema = z.object({
  minMinutes: z.number().int().nonnegative(),
  maxMinutes: z.number().int().positive(),
  label: z.string().min(1),
});
export type EtaBand = z.infer<typeof EtaBandSchema>;

/**
 * Uma zona de entrega, como o cliente a ve.
 *
 * `freightCents` viaja como `Money` — e e o **preco de tabela**, nao o frete cobrado: o frete
 * cobrado sai da etapa 8 da cascata, que sabe do limiar de frete gratis e do cupom. Uma tela
 * que somasse este valor ao subtotal estaria refazendo a etapa 8 no cliente.
 */
export const DeliveryZoneSchema = z.object({
  id: Uuidv7Schema,
  name: z.string().min(1),
  kind: DeliveryZoneKindSchema,
  freight: NonNegativeMoneySchema,
  /** `null` = a zona nao tem limiar de frete gratis. */
  freeAbove: NonNegativeMoneySchema.nullable(),
  minOrder: NonNegativeMoneySchema,
  eta: EtaBandSchema,
  /** Municipio vizinho entra como **orcamento**: a loja responde antes de aceitar. */
  requiresQuote: z.boolean(),
});
export type DeliveryZone = z.infer<typeof DeliveryZoneSchema>;

export const DeliveryZonesResponseSchema = z.object({
  storeId: Uuidv7Schema,
  data: z.array(DeliveryZoneSchema),
});
export type DeliveryZonesResponse = z.infer<typeof DeliveryZonesResponseSchema>;

// ---------------------------------------------------------------------------
// Resposta da cotacao
// ---------------------------------------------------------------------------

export const QuoteFulfillmentSchema = z.object({
  mode: CartFulfillmentModeSchema,
  /** `null` em `PICKUP` — retirada nao tem zona. */
  zoneId: Uuidv7Schema.nullable(),
  /** O frete **cobrado**, ja depois da etapa 8. Zero quando gratis; zero em `PICKUP`. */
  deliveryFee: NonNegativeMoneySchema,
  etaBand: EtaBandSchema,
  /** `GET /v1/delivery/slots` e `P` (`API §6.10`): sempre `null` no MVP. */
  scheduled: z.null(),
  minOrderValue: NonNegativeMoneySchema.nullable(),
});
export type QuoteFulfillment = z.infer<typeof QuoteFulfillmentSchema>;

/**
 * Uma linha cotada. `unitPrice` e o **preco unitario efetivo** (`effectiveUnitPriceCents` de
 * `CONTRACTS §3.4`), ja depois das etapas 4-6 e do teto CMED da etapa 9.
 *
 * `lineTotal` **nao** desconta a parcela do cupom: o cupom e desconto de PEDIDO (etapa 7) e
 * aparece uma vez so, em `totals.discounts`. Somar as linhas e subtrair `couponDiscount`
 * reproduz o total — que e literalmente a asserção final do motor (`CONTRACTS §3.2`).
 */
export const QuoteLineSchema = z.object({
  itemId: Uuidv7Schema,
  productId: Uuidv7Schema,
  displayName: z.string().min(1),
  quantity: z.number().int().positive(),
  unitPrice: NonNegativeMoneySchema,
  lineTotal: NonNegativeMoneySchema,
  /** `null` em nao medicamento. Viaja porque o RT e o balcao precisam conferir (REG-032). */
  cmedCeiling: NonNegativeMoneySchema.nullable(),
  availability: AvailabilityBandSchema,
});
export type QuoteLine = z.infer<typeof QuoteLineSchema>;

/**
 * O diff explicito — `API §6.20.6`: *"quando algo mudou desde o carrinho; e o que a UI
 * destaca"*.
 *
 * `PRICE_CHANGED` aqui e **aviso**, nao erro: a rota cuja funcao e recalcular nao pode recusar
 * o proprio recalculo. O `409 PRICE_CHANGED` do `CONTRACTS §6` pertence a `POST /v1/orders`,
 * onde o cliente **apresenta** uma cotacao anterior. Os dois usam o mesmo nome canonico porque
 * `CONTRACTS §6` proibe variante — nao ha `PRICE_MOVED` nem `QUOTE_STALE`.
 */
export const QuoteChangeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('PRICE_CHANGED'),
    itemId: Uuidv7Schema,
    from: MoneySchema,
    to: MoneySchema,
  }),
  z.object({
    kind: z.literal('QUANTITY_ADJUSTED'),
    itemId: Uuidv7Schema,
    from: z.number().int().positive(),
    to: z.number().int().nonnegative(),
    reason: z.enum(['LOW_STOCK', 'QUANTITY_LIMIT']),
  }),
  z.object({
    kind: z.literal('ITEM_REMOVED'),
    itemId: Uuidv7Schema,
    reason: z.enum(['OUT_OF_STOCK', 'REGULATORY']),
  }),
  z.object({
    kind: z.literal('COUPON_DROPPED'),
    code: z.string().min(1),
    reason: z.string().min(1),
  }),
]);
export type QuoteChange = z.infer<typeof QuoteChangeSchema>;

/** Os nomes de `CONTRACTS §3.4`, embrulhados em `Money` (`API §1.8`). */
export const QuoteTotalsSchema = z.object({
  subtotal: NonNegativeMoneySchema,
  /** `itemDiscountsCents + couponDiscountCents` — o desconto de PRODUTO, sem o frete. */
  discounts: NonNegativeMoneySchema,
  deliveryFee: NonNegativeMoneySchema,
  total: NonNegativeMoneySchema,
  /** `itemDiscounts + coupon + shippingDiscount` (`CONTRACTS §3.4`, `savingsCents`). */
  savings: NonNegativeMoneySchema,
});
export type QuoteTotals = z.infer<typeof QuoteTotalsSchema>;

/**
 * Uma regra aplicada, para explicabilidade — `AppliedRule` de `CONTRACTS §3.4`.
 *
 * Existe na resposta porque `API §6.11` descreve exatamente esta informacao ("regra, id, base
 * de calculo, valor — e o que o balconista consulta quando o cliente questiona o preco") e a
 * rota que a serviria (`GET /v1/promotions/applied`) e `P`. Sem ela, a unica forma de explicar
 * um total no balcao seria refazer a conta a mao.
 */
export const QuoteAppliedRuleSchema = z.object({
  scope: z.enum(['LINE', 'ORDER', 'SHIPPING']),
  /**
   * **`EQUALIZACAO_DE_REDE` entra AQUI uma release antes de o motor emiti-lo, e a razao e que o
   * cliente valida a RESPOSTA contra este enum.**
   *
   * `packages/api-client/src/http.ts:396-406` faz `spec.response.safeParse(body)` e, na falha,
   * lanca `RESPOSTA_NAO_CONFORME` — nao degrada, nao ignora o campo: **derruba a chamada**. Como o
   * PWA e servido por service worker e um navegador pode ficar dias com o bundle anterior, ligar o
   * motor no mesmo deploy do enum faria o checkout de quem nao atualizou morrer no
   * `POST /v1/checkout/quote`.
   *
   * Entao a ordem e: primeiro o fio aceita o valor (este commit, com ninguem emitindo), depois o
   * motor passa a emitir. O sexto valor e ADR-0026 — a diferenca ate o menor preco praticado na
   * rede, que **nao e uma `Promotion`**: nao tem `promotion.id` nem janela de vigencia.
   */
  kind: z.enum([
    'PROMOTION',
    'CLUB_PRICE',
    'COUPON',
    'FREE_SHIPPING',
    'CEILING_CAP',
    'EQUALIZACAO_DE_REDE',
  ]),
  refId: z.string().min(1),
  itemId: Uuidv7Schema.nullable(),
  amount: NonNegativeMoneySchema,
  reason: z.string().min(1),
  /** A etapa da cascata que produziu a regra (`CONTRACTS §3.1`). */
  stage: z.number().int().min(0).max(11),
});
export type QuoteAppliedRule = z.infer<typeof QuoteAppliedRuleSchema>;

export const QuoteResponseSchema = z.object({
  /** TTL de 10 min (`CONTRACTS §6`); exigido por `POST /v1/orders`. */
  quoteId: Uuidv7Schema,
  /**
   * Hash do CONTEUDO (linhas + valores + regras aplicadas), para o PWA detectar divergencia
   * offline (X-14) e para `POST /v1/orders` ecoar. E hash, nao assinatura: ele nao autoriza
   * nada — o servidor recalcula de qualquer jeito (`SECURITY §8.1`, regra 2: "o total e
   * recalculado tres vezes").
   */
  quoteHash: z.string().regex(/^[0-9a-f]{64}$/, 'quoteHash é sha256 em hexadecimal minúsculo.'),
  expiresAt: InstantSchema,
  computedAt: InstantSchema,
  /** Versao do motor que produziu estes numeros (`CONTRACTS §3.4`, `engineVersion`). */
  engineVersion: z.string().min(1),
  storeId: Uuidv7Schema,
  cartId: Uuidv7Schema,
  /** A versao do carrinho cotada. `POST /v1/orders` compara (`API §4.2`). */
  cartVersion: z.number().int().nonnegative(),
  /** O `pricingSnapshotId` observado. Mudou desde a ultima cotacao ⇒ `changes[PRICE_CHANGED]`. */
  pricingSnapshotId: Uuidv7Schema,
  fulfillment: QuoteFulfillmentSchema,
  items: z.array(QuoteLineSchema),
  changes: z.array(QuoteChangeSchema),
  totals: QuoteTotalsSchema,
  appliedRules: z.array(QuoteAppliedRuleSchema),
  /** `payment.methods` do perfil, menos o que a instancia nao consegue oferecer hoje. */
  availablePaymentMethods: z.array(PaymentMethodSchema),
  requiresPrescription: z.boolean(),
  /** "Tenha a receita em mãos na entrega." `null` quando nenhuma linha exige. */
  prescriptionNotice: z.string().nullable(),
});
export type QuoteResponse = z.infer<typeof QuoteResponseSchema>;
